import type { ReactNode } from "react";
import { Fragment } from "react";
import { AnimatePresence, motion } from "motion/react";

import type { SVGString } from "*.svg";

import { Static } from "@scripts/bondlinkStatic";
import { Svg } from "@scripts/react/components/Svg";
import { klass, klassProp } from "@scripts/react/util/classnames";

import checkmark from "@svgs/checkmark.svg";
import information from "@svgs/information.svg";
import warning from "@svgs/warning.svg";

import { CloseXButton } from "./Button";

type NotificationTypes = "success" | "info" | "warning" | "danger";
type NotificationIcons = { [K in NotificationTypes]: SVGString };

type DismissAction = { dismissAction: (id: string) => void };

export type NotificationBaseProps = {
  type: NotificationTypes;
  children: ReactNode;
  id: string;
  title: string;
};

type NotificationProps = NotificationBaseProps & DismissAction;

const notificationTypeToStyle = (type: NotificationTypes) => klassProp(`notification-${type}`);

export const notificationIcons: NotificationIcons = {
  "success": checkmark,
  "info": information,
  "warning": warning,
  "danger": warning,
};

const NotificationBody = (props: NotificationProps) => (
  <Fragment>
    <div {...klass("header", "inverted")}>
      <div {...klass("title")}>
        <Svg src={notificationIcons[props.type]} />
        <h5>{props.title}</h5>
      </div>
      <CloseXButton onClick={() => props.dismissAction(props.id)} />
    </div>
    <div {...klass("body", notificationTypeToStyle(props.type))}>
      {props.children}
    </div>
  </Fragment>
);

export const Notification = (props: NotificationProps) =>
  <div
    role="alert"
    key={props.id}
    {...klass("notification", notificationTypeToStyle(props.type))}
  >
    <NotificationBody
      id={props.id}
      type={props.type}
      dismissAction={props.dismissAction}
      title={props.title}
    >
      {props.children}
    </NotificationBody>
  </div>;

export const NotificationContainer = (props: { notifications: NotificationBaseProps[] } & DismissAction) =>
  <div {...klass("notification-container")}>
    <AnimatePresence>
      {props.notifications.map(notification =>
        <motion.div
          key={`motion-div-${notification.id}`}
          initial={{ opacity: 0, marginBottom: "0", height: "0", transform: "translateY(100%)" }}
          animate={{ opacity: 1, marginBottom: "1rem", height: "9rem", transform: "translateY(0)" }}
          exit={{ opacity: 0, marginBottom: "0", height: "0" }}
          transition={{ duration: Static.baseTransitionDelay / 1000 }}
        >
          <Notification
            key={notification.id}
            id={notification.id}
            type={notification.type}
            title={notification.title}
            dismissAction={props.dismissAction}
          >
            {notification.children}
          </Notification>
        </motion.div>
      )}
    </AnimatePresence>
  </div>;
